import { RefCallback, useCallback, useEffect, useState } from 'react';

/**
 * Custom hook to determine if an element is visible within the viewport.
 *
 * @param {boolean} isRefVisibleInitially - Initial visibility state of the element (ref). Defaults to false.
 * @returns {object} An object containing:
 *  - `isVisible`: A boolean indicating the visibility of the element.
 *  - `ref`: A ref callback function to be attached to the element to monitor.
 */
export function useOnScreen(isRefVisibleInitially: boolean = false): {
    isVisible: boolean;
    ref: RefCallback<HTMLElement>;
} {
    const [isVisible, setIsVisible] = useState(isRefVisibleInitially);
    const [refElement, setRefElement] = useState<HTMLElement | null>(null);

    const setRef = useCallback((node: HTMLElement | null) => {
        if (node !== null) {
            setRefElement(node);
        }
    }, []);

    useEffect(() => {
        if (refElement) {
            const observer = new IntersectionObserver(([entry]) => setIsVisible(entry?.isIntersecting === true));

            observer.observe(refElement);

            return () => {
                observer.disconnect();
            };
        }
    }, [refElement]);

    return { isVisible, ref: setRef };
}
